/**
 * Pay period math.
 *
 * Cycles supported: weekly, biweekly, semimonthly, monthly. Each profile
 * carries:
 *   - payDay: number — primary anchor day. Meaning depends on cycle:
 *       weekly      => 0..6 day of week (0=Sun, 1=Mon, ...)
 *       monthly     => 1..31 day of month
 *       semimonthly => 1..31 first pay day of the month
 *   - payDay2?: number — semimonthly only: the second pay day (1..31).
 *   - payAnchor?: string — biweekly only: ISO date of any past pay day.
 *
 * `getPayPeriod(profile, today)` returns the period containing `today`:
 *   { start, end, totalDays, dayInPeriod, daysRemaining }
 *
 *   - start/end are local-midnight Dates, both inclusive.
 *   - totalDays is the number of calendar days in the period.
 *   - dayInPeriod is 1-based: 1 on the start day, totalDays on the end day.
 *   - daysRemaining counts whole days from today (exclusive) to end (inclusive).
 */

import {
  addDays,
  clampDayToMonth,
  diffInDays,
  fromIsoDate,
  isBetweenInclusive,
  startOfDay,
} from './date';

export type PayCycle = 'weekly' | 'biweekly' | 'semimonthly' | 'monthly';

export type PayProfile = {
  payCycle: PayCycle;
  payDay: number;
  payDay2?: number;
  payAnchor?: string;
};

export interface PayPeriod {
  start: Date;
  end: Date;
  totalDays: number;
  /** 1-based index of `today` within the period. */
  dayInPeriod: number;
  /** Whole days from today (exclusive) to end (inclusive). 0 on the last day. */
  daysRemaining: number;
}

function buildPeriod(start: Date, end: Date, today: Date): PayPeriod {
  const startD = startOfDay(start);
  const endD = startOfDay(end);
  const totalDays = diffInDays(startD, endD) + 1;
  const dayInPeriod = Math.min(
    Math.max(1, diffInDays(startD, today) + 1),
    totalDays,
  );
  const daysRemaining = Math.max(0, diffInDays(today, endD));
  return { start: startD, end: endD, totalDays, dayInPeriod, daysRemaining };
}

function weeklyPeriod(profile: PayProfile, today: Date): PayPeriod {
  const dow = ((profile.payDay % 7) + 7) % 7;
  const todayDow = today.getDay();
  // How many days back to the most recent payDay (0..6).
  const back = (todayDow - dow + 7) % 7;
  const start = addDays(today, -back);
  const end = addDays(start, 6);
  return buildPeriod(start, end, today);
}

function biweeklyPeriod(profile: PayProfile, today: Date): PayPeriod {
  // Without an anchor, fall back to "every other week starting last <payDay>".
  const anchor = profile.payAnchor
    ? fromIsoDate(profile.payAnchor)
    : weeklyPeriod(profile, today).start;
  const days = diffInDays(anchor, today);
  // Ensure non-negative remainder even when anchor is in the future.
  const rem = ((days % 14) + 14) % 14;
  const start = addDays(today, -rem);
  const end = addDays(start, 13);
  return buildPeriod(start, end, today);
}

function semimonthlyPeriod(profile: PayProfile, today: Date): PayPeriod {
  // Two pay days per month. Default to (15, last) when payDay2 is missing.
  const a = clampDayToMonth(today.getFullYear(), today.getMonth() + 1, profile.payDay);
  const bRaw = profile.payDay2 ?? 31;
  const b = clampDayToMonth(today.getFullYear(), today.getMonth() + 1, bRaw);
  const [first, second] = a <= b ? [a, b] : [b, a];

  const y = today.getFullYear();
  const m = today.getMonth(); // 0-indexed
  const day = today.getDate();

  if (day >= first && day < second) {
    const start = new Date(y, m, first);
    const end = new Date(y, m, second - 1);
    return buildPeriod(start, end, today);
  }

  if (day >= second) {
    // Period: [second this month, first next month - 1]
    const start = new Date(y, m, second);
    const nextFirst = clampDayToMonth(y, m + 2, profile.payDay);
    const end = addDays(new Date(y, m + 1, nextFirst), -1);
    return buildPeriod(start, end, today);
  }

  // day < first: previous period started on `second` of last month
  const prevSecond = clampDayToMonth(y, m, profile.payDay2 ?? 31);
  const start = new Date(y, m - 1, prevSecond);
  const end = addDays(new Date(y, m, first), -1);
  return buildPeriod(start, end, today);
}

function monthlyPeriod(profile: PayProfile, today: Date): PayPeriod {
  const y = today.getFullYear();
  const m = today.getMonth();
  const thisMonthPay = clampDayToMonth(y, m + 1, profile.payDay);
  const start =
    today.getDate() >= thisMonthPay
      ? new Date(y, m, thisMonthPay)
      : new Date(y, m - 1, clampDayToMonth(y, m, profile.payDay));
  const nextMonthPay = clampDayToMonth(
    start.getFullYear(),
    start.getMonth() + 2,
    profile.payDay,
  );
  const end = addDays(
    new Date(start.getFullYear(), start.getMonth() + 1, nextMonthPay),
    -1,
  );
  return buildPeriod(start, end, today);
}

export function getPayPeriod(
  profile: PayProfile,
  today: Date = new Date(),
): PayPeriod {
  const t = startOfDay(today);
  switch (profile.payCycle) {
    case 'weekly':
      return weeklyPeriod(profile, t);
    case 'biweekly':
      return biweeklyPeriod(profile, t);
    case 'semimonthly':
      return semimonthlyPeriod(profile, t);
    case 'monthly':
      return monthlyPeriod(profile, t);
  }
}

/** Return the next pay date strictly after `today`. */
export function getNextPayDate(
  profile: PayProfile,
  today: Date = new Date(),
): Date {
  const period = getPayPeriod(profile, today);
  return addDays(period.end, 1);
}

/** True if the given date falls within the current pay period. */
export function isInCurrentPeriod(
  profile: PayProfile,
  date: Date,
  today: Date = new Date(),
): boolean {
  const period = getPayPeriod(profile, today);
  return isBetweenInclusive(date, period.start, period.end);
}
